import { useRef, type ComponentProps } from 'react'

import { cn } from '@/utils'
import _compact from 'lodash/compact'
import _indexOf from 'lodash/indexOf'

import { CELL_SELECT_COLORS } from '@rundown-studio/consts'

import { useCueState } from '../cue/CueContext'
import { useRundownSnap } from '../rundown-page.store'
import { useCellDerivedState, useCellState } from './CellContext'

import { gridState, gridStateActions } from '../grid/grid.store'
import { updateRundownColumn } from '../../../firestore'
import { triggerToast } from '@/shared/ui/toast'

import * as logger from '@/utils/logger'
import { EMPTY_ARRAY } from '@/utils/primitives'

import { CellClickOutsideController } from './cell.hooks'
import { CellFrame } from './CellFrame'
import { CellToolbar } from './toolbar/CellToolbar'

import { Menu, MenuItem } from '@/components/interactives/dropdown-menu'
import { faSquare } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  CellToolbarItemDiscard,
  CellToolbarItemSave,
  CellToolbarItemEdit,
} from './toolbar/CellToolbar.items'
import { upsertCell } from './cell.requests'

/**
 * Renders a `cell` of column type `select`.
 */
export function CellTypeSelect() {
  const { id: rundownId } = useRundownSnap()
  const { cueId } = useCueState()
  const { cell, col } = useCellState()

  // Assert that this `Column` is of the type that has options
  if (col.type !== 'select') {
    throw new Error('Invalid column data')
  }

  const { isReadOnly, isEditing } = useCellDerivedState()

  const selectOptions = col.options || EMPTY_ARRAY

  const selectedValue = cell?.content.selected || '' // Empty string indicates no option selected
  const selectedValueColor =
    CELL_SELECT_COLORS[_indexOf(selectOptions, selectedValue)]

  const isValidSelection = Boolean(
    selectedValue && selectOptions.includes(selectedValue),
  )
  const isOptionsEmpty = !selectOptions.length

  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  // #region ============== EVENTS

  // TODO: Move into actions
  const handleUpdateSelected = (newSelectedValue: string) => {
    gridState.editor.saveStatus = { status: 'pending' }

    upsertCell(cell, col.id, cueId, {
      content: {
        selected: selectedValue === newSelectedValue ? '' : newSelectedValue,
      },
    })
      .then(() => {
        gridState.editor.saveStatus = { status: 'success' }
        gridStateActions.editor.discard()
      })
      .catch((err) => {
        logger.error('[GridState] Save failed:', err)

        triggerToast({
          type: 'fail',
          title: `Save failed.`,
          body: 'Please try again or copy your changes and refresh the page.',
        })

        gridState.editor.saveStatus = {
          status: 'error',
          error: err instanceof Error ? err : new Error('Save failed'),
        }
      })
  }

  // TODO: Move into actions
  const handleSaveOptions = () => {
    const textAreaElm = textareaRef.current
    if (!textAreaElm) return

    const newSelectOptions = textAreaElm.value.split('\n')
    if (newSelectOptions.length < 0) return

    const newOptionsCleaned = _compact(newSelectOptions)

    gridState.editor.saveStatus = { status: 'pending' }

    updateRundownColumn(rundownId, col.id, { options: newOptionsCleaned })
      .then(() => {
        gridState.editor.saveStatus = { status: 'success' }
        gridStateActions.editor.discard()
      })
      .catch((err) => {
        logger.error('[GridState] Save failed:', err)

        triggerToast({
          type: 'fail',
          title: `Save failed.`,
          body: 'Please try again or copy your changes and refresh the page.',
        })

        gridState.editor.saveStatus = {
          status: 'error',
          error: err instanceof Error ? err : new Error('Save failed'),
        }
      })
  }

  // #endregion

  if (isEditing) {
    return (
      <CellFrame>
        <ContentWrapper>
          <textarea
            ref={textareaRef}
            autoFocus
            placeholder={'Option 1…\nOption 2…\nOption 3…'}
            defaultValue={selectOptions.join('\n')}
            rows={4}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.metaKey) {
                handleSaveOptions()
                return
              }
              if (e.key === 'Escape') {
                gridStateActions.editor.discard()
                return
              }
            }}
            className="p-1.5 bg-transparent border border-gray-700 rounded-sm text-sm w-full whitespace-pre-line"
          />
          <div className="mb-8 text-xs opacity-50">One option per line.</div>
        </ContentWrapper>

        <CellClickOutsideController
          handler={() => {
            handleSaveOptions()
          }}
        />

        <CellToolbar
          onKeyTab={() => {
            textareaRef.current?.focus({ preventScroll: true })
          }}
        >
          <CellToolbarItemDiscard onClick={gridStateActions.editor.discard} />

          <CellToolbarItemSave onClick={handleSaveOptions} />
        </CellToolbar>
      </CellFrame>
    )
  }

  return (
    <CellFrame>
      <ContentWrapper>
        <SelectComponent
          isReadOnly={isReadOnly}
          selectedValue={isValidSelection ? selectedValue : '–'}
          selectedValueColor={selectedValueColor}
          onDoubleClick={(e) => e.stopPropagation()} // Prevent triggering edit for multiple clicks
        >
          {isOptionsEmpty ? (
            <MenuItem key="empty" label="No options found." disabled />
          ) : (
            selectOptions.map((option, i) => (
              <MenuItem
                key={option}
                label={option}
                active={selectedValue === option}
                onClick={() => handleUpdateSelected(option)}
                IconComponent={
                  <FontAwesomeIcon
                    icon={faSquare}
                    className="mr-2"
                    style={{
                      color: CELL_SELECT_COLORS[i],
                    }}
                  />
                }
              />
            ))
          )}
        </SelectComponent>
      </ContentWrapper>

      <CellToolbar>
        <CellToolbarItemEdit />
      </CellToolbar>
    </CellFrame>
  )
}

function ContentWrapper({ children }: ComponentProps<'div'>) {
  return (
    <div role="none" className="px-3 py-2 select-none">
      {children}
    </div>
  )
}

function SelectComponent({
  isReadOnly,
  selectedValue,
  selectedValueColor,
  children,
  ...props
}: ComponentProps<typeof Menu> & {
  isReadOnly: boolean
  selectedValue: string
  selectedValueColor: string | undefined
}) {
  return (
    <Menu
      disabled={isReadOnly}
      placement="bottom-end"
      label={selectedValue}
      className={cn(
        'w-full gap-1 py-1.5 font-normal bg-gray-950 min-h-9 h-auto',
        isReadOnly ? 'cursor-not-allowed!' : 'hover:brightness-110',
        selectedValueColor ? 'text-white' : 'bg-black text-gray-400',
        'disabled:opacity-100', // Override `disabled` opacity from `<Menu>`
        'break-all [&>div]:whitespace-normal', // Override `truncate` on label elm.
      )}
      style={{
        backgroundColor: selectedValueColor,
      }}
      {...props}
    >
      {children}
    </Menu>
  )
}

/**
 * Static rendering version of the selection component for CellTypeSelect.
 * Styled to look exactly like `SelectComponent` with no interactions.
 *
 * Only used for Rundown Output where no interaction is needed.
 */
export function CellTypeSelectStatic({
  selectedValue,
  selectedValueColor,
  ...props
}: ComponentProps<'div'> & {
  selectedValue: string
  selectedValueColor: string | undefined
}) {
  return (
    <div
      className={cn(
        [
          // Replicate styling of `button` inside `<Menu>`
          'flex items-center justify-between',
          'px-3 py-1.5 min-h-9',
          'rounded-sm',
          'text-sm break-all',
          'select-none',
        ],
        // Shared styles
        'bg-gray-950',
        selectedValueColor ? 'text-white' : 'bg-black text-gray-400',
      )}
      style={{
        backgroundColor: selectedValueColor,
      }}
      {...props}
    >
      <div role="none" className="grow">
        {selectedValue || '–'}
      </div>
    </div>
  )
}
